import { api, config, mui, React, ts, ui } from '_core';

import { Params } from './types';

type ChartProps = {
  id: string;
  jobId: number;
  file: string;
  params: Params;
};

const Chart = ({ id: _id, jobId, file, params }: ChartProps) => {
  const [downloading, setDownloading] = React.useState(false);
  const [portfolio, setPortfolio] = React.useState<ts.types.appPortfolio.Portfolio | null>(null);
  const [loadingPortfolio, setLoadingPortfolio] = React.useState(true);

  const isPortfoliosEnabled = config.features.portfolios_product_enabled;
  const hasSyncEnabled = params.sync_with_portfolios_app && isPortfoliosEnabled;
  const hasPortfolioSelected = hasSyncEnabled && params.app_portfolio_id;

  const endpointUrl = React.useMemo(() => {
    if (config.environment === 'production') return 'portfolios.finsera.com';
    if (config.environment === 'staging') return 'portfolios.finserastg.net';
    if (config.environment === 'development') return 'portfolios.finseradev.net';
    const dom = process.env.FINSERA_PROXY_API.split('//')[1].split('/')[0].split('.').slice(1).join('.');
    return `portfolios.${dom}`;
  }, []);

  // Load portfolio details if selected
  React.useEffect(() => {
    const loadPortfolio = async () => {
      if (hasPortfolioSelected && params.app_portfolio_id) {
        setLoadingPortfolio(true);
        try {
          const response = await api.app_portfolios.get(params.app_portfolio_id);
          setPortfolio(response.data);
        } catch (error) {
          console.error('Error loading portfolio:', error);
          setPortfolio(null);
        } finally {
          setLoadingPortfolio(false);
        }
      } else {
        setPortfolio(null);
        setLoadingPortfolio(false);
      }
    };

    loadPortfolio();
  }, [hasPortfolioSelected, params.app_portfolio_id]);

  const handleDownload = () => {
    setDownloading(true);
    const s3FileObject = {} as Record<string, string>;
    s3FileObject['export_portfolio_holdings'] = file;

    api.s3
      .signPaths(s3FileObject, jobId, 'jobs')
      .then((signed: any) => {
        const url = signed.data.jobs[jobId]['export_portfolio_holdings'];

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', url);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloading(false);
      })
      .catch((error: any) => {
        console.error('Error downloading:', error);
        setDownloading(false);
      });
  };

  if (loadingPortfolio) {
    return <ui.CenteredLoader />;
  }

  return (
    <mui.core.Box
      mt={-10}
      display="flex"
      flexDirection="column"
      flex={1}
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <mui.core.Card
        sx={{
          textAlign: 'center',
          minWidth: 600,
          py: 10,
        }}
      >
        <mui.core.CardContent>
          {hasPortfolioSelected && portfolio && (
            <mui.core.Box mb={3}>
              <mui.core.Typography variant="h6" gutterBottom>
                Portfolio in the portfolios app:{' '}
                <a target="_blank" rel="noreferrer" href={`https://${endpointUrl}/portfolios/${portfolio.id}`}>
                  {portfolio.name}
                </a>
                <mui.core.Tooltip
                  title="You need to be logged in to the same organization in the portfolios app to access this link"
                  placement="top"
                >
                  <mui.icons.InfoOutlined
                    sx={{
                      ml: 1,
                      fontSize: '1rem',
                      color: 'text.secondary',
                    }}
                  />
                </mui.core.Tooltip>
              </mui.core.Typography>
              <mui.core.Typography variant="body2" color="text.secondary" gutterBottom>
                Generation of the report will sync with this portfolio in the portfolios app
              </mui.core.Typography>
            </mui.core.Box>
          )}

          <ui.Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            loading={downloading}
            progressText="Downloading..."
            disabled={downloading}
            size="large"
            fullWidth
          >
            Download Latest Holdings Data
          </ui.Button>
        </mui.core.CardContent>
      </mui.core.Card>
    </mui.core.Box>
  );
};

export default Chart;
